/** Presentation helpers. Pure, so they are cheap to unit test. */

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

export function formatDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

export function relativeDate(iso: string, now: number = Date.now()): string {
  const then = new Date(`${iso}T00:00:00`).getTime();
  if (Number.isNaN(then)) return iso;

  const days = Math.round((now - then) / 86_400_000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days} days ago`;

  const months = Math.round(days / 30);
  if (months < 12) return `${months} ${months === 1 ? "month" : "months"} ago`;

  const years = Math.round(months / 12);
  return `${years} ${years === 1 ? "year" : "years"} ago`;
}

/** Indian currency shorthand: 1,20,000 -> ₹1.2 L, 1,20,00,000 -> ₹1.2 Cr. */
export function formatINR(value: number): string {
  if (value >= 10_000_000) return `₹${trimZeros(value / 10_000_000)} Cr`;
  if (value >= 100_000) return `₹${trimZeros(value / 100_000)} L`;
  return `₹${value.toLocaleString("en-IN")}`;
}

function trimZeros(n: number): string {
  return n.toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
}

export function formatNumber(value: number): string {
  return value.toLocaleString("en-IN");
}

export function formatArea(sqft: number): string {
  return `${formatNumber(sqft)} sq.ft.`;
}

/** Feet with inches, the way a drawing annotates it: 14'-6". */
export function feet(value: number): string {
  let whole = Math.floor(value);
  let inches = Math.round((value - whole) * 12);
  if (inches === 12) {
    whole += 1;
    inches = 0;
  }
  return `${whole}'${inches ? `-${inches}"` : ""}`;
}

export function initials(name: string): string {
  return name
    .replace(/^(Ar\.|Er\.)\s*/, "")
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0))
    .join("")
    .toUpperCase();
}

export function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** Escapes a term before it is used to build a highlight RegExp. */
export function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
